import { BulletFillIcon, ChevronDownOutlineIcon, ChevronUpOutlineIcon } from '@applyboard/ui-icons';
import * as RadixPopover from '@radix-ui/react-popover';
import { useMultipleSelection, useSelect } from 'downshift';
import React from 'react';
import { Divider } from '../Divider';
import { FieldCompatibleProps } from '../Field';
import { Text } from '../Text';
import { IconType, IntentProps, Responsive, useFullTheme } from '~/design';
import { InternalCheckboxButton } from '~/internal/InternalCheckbox/InternalCheckbox';
import { findChildrenOfType, useResponsive, useSelectStyling } from '~/utils';
import {
  FormControlContainer,
  FormControlContainerProps,
  useFormControlProps,
} from '~/internal/FormControlContainer';
import { IconWrapper } from '~/internal';

interface Option {
  disabled?: boolean;
  label: string;
  value: string;
}

interface MultiSelectContextContextProps {
  closeMenu: () => void;
  disabled?: boolean;
  getItemProps: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  groups: MultiSelectOptionGroupProps[];
  highlightedIndex: number;
  isOpen: boolean;
  onBlur?: MultiSelectProps['onBlur'];
  onChange: MultiSelectProps['onChange'];
  optionsWithGroups: OptionWithGroup[];
  values: MultiSelectProps['value'];
}

const MultiSelectContext = React.createContext<MultiSelectContextContextProps | null>(null);

type PlaceholderVariant = 'truncate' | 'indicator' | 'groups' | 'indicatorOnly';

export interface MultiSelectProps extends FieldCompatibleProps<string[]>, IntentProps {
  /** Used to provide the description of the MultiSelect, must be used when label is undefined.*/
  'aria-label'?: string;

  /** The children of the MultiSelect. */
  children: React.ReactNode;

  /** If true, the MultiSelect cannot be interacted with. */
  disabled?: boolean;

  /** The emphasis of the MultiSelect against its background. (default: outlined) */
  emphasis?: 'highlighted' | 'outlined';

  labelPosition?: FormControlContainerProps['labelPosition'];

  /** The icon that appears on the left side of the MultiSelect. */
  leadIcon?: IconType;

  /** How the dropdown menu will align with the trigger. `start` will align the start (left) of the
   * menu with the start of the trigger. `end` will align the end (right) of the menu with the end
   * of the trigger. `end` is useful when you have a MultiSelect at the right side of the screen.
   * (default: start) */
  menuAlignment?: 'start' | 'end';

  /** The text that is presented by default the MultiSelect when if no selection has been made. */
  placeholder?: string;

  /** The format of the placeholder. "truncate" will show all options as a comma-separated string
   * and truncate the text when it exceeds the max-width. "indicator" will show an icon indicating
   * that options have been selected. "groups" will behave the same as "truncate", except that
   * options that belong to a group will include the group label as a prefix. "indicatorOnly"
   * will hide all placeholder text except an icon indicating options have been selected
   * (default: truncate) */
  placeholderVariant?: Responsive<PlaceholderVariant> | PlaceholderVariant;

  /** The size of the MultiSelect. */
  size?: 'sm' | 'md' | 'lg';
}

export function MultiSelect({
  'aria-label': ariaLabel,
  children,
  disabled = false,
  emphasis = 'outlined',
  helpText,
  intent = 'secondary',
  invalid,
  label,
  labelPosition,
  leadIcon,
  menuAlignment = 'start',
  onBlur,
  onChange,
  placeholder = 'Select',
  placeholderVariant = 'truncate',
  required = false,
  size,
  value,
}: MultiSelectProps) {
  if (!label && !ariaLabel) {
    throw Error('MultiSelect component requires aria-label prop when label prop is undefined.');
  }

  const optionsWithGroups = mapOptionsToGroups(children);

  const { getDropdownProps, selectedItems } = useMultipleSelection({
    initialSelectedItems: value,
    selectedItems: value,
  });

  const groups = (
    findChildrenOfType(children, [
      MultiSelect.OptionGroup,
    ]) as unknown as React.ReactElement<MultiSelectOptionGroupProps>[]
  ).map(c => c.props);

  const {
    getItemProps,
    getLabelProps,
    getMenuProps,
    getToggleButtonProps,
    highlightedIndex,
    isOpen,
    closeMenu,
  } = useSelect({
    selectedItem: null,
    items: optionsWithGroups,
    onStateChange: ({ type, selectedItem: newSelectedItem }) => {
      switch (type) {
        case useSelect.stateChangeTypes.ToggleButtonBlur:
        case useSelect.stateChangeTypes.ToggleButtonKeyDownEscape:
          onBlur?.();
          break;
        case useSelect.stateChangeTypes.ToggleButtonKeyDownEnter:
        case useSelect.stateChangeTypes.ToggleButtonKeyDownSpaceButton:
          if (newSelectedItem) {
            const isAlreadySelected = value.includes(newSelectedItem.value);
            onChange(
              isAlreadySelected
                ? value.filter(otherValue => otherValue !== newSelectedItem.value)
                : [...value, newSelectedItem.value],
            );
          }
          break;
        default:
          break;
      }
    },
    stateReducer: ({ highlightedIndex }, actionAndChanges) => {
      const { changes, type } = actionAndChanges;
      switch (type) {
        case useSelect.stateChangeTypes.ToggleButtonKeyDownEnter:
        case useSelect.stateChangeTypes.ToggleButtonKeyDownSpaceButton:
        case useSelect.stateChangeTypes.ItemClick:
          return {
            ...changes,
            highlightedIndex, // Keep the same item selected
            isOpen: true,
          };
      }
      return changes;
    },
    isItemDisabled(item) {
      return !!item.disabled;
    },
  });

  const { adjustedIntent, helpTextId } = useFormControlProps({ disabled, intent, invalid });

  return (
    <MultiSelectContext.Provider
      value={{
        closeMenu,
        disabled,
        getItemProps,
        groups,
        highlightedIndex,
        isOpen,
        onBlur,
        onChange,
        optionsWithGroups,
        values: selectedItems,
      }}
    >
      <RadixPopover.Root open={isOpen}>
        <FormControlContainer
          disabled={disabled}
          helpText={helpText}
          helpTextId={helpTextId}
          intent={intent}
          invalid={invalid}
          label={label}
          labelId={getToggleButtonProps()['aria-labelledby']}
          labelPosition={labelPosition}
          required={required}
          {...getLabelProps()}
        >
          <Trigger
            aria-label={ariaLabel}
            emphasis={emphasis}
            getDropdownProps={getDropdownProps}
            getToggleButtonProps={getToggleButtonProps}
            leadIcon={leadIcon}
            intent={adjustedIntent}
            invalid={invalid}
            placeholder={placeholder}
            placeholderVariant={placeholderVariant}
            size={size}
          />
        </FormControlContainer>

        <Menu getMenuProps={getMenuProps} menuAlignment={menuAlignment}>
          {children}
        </Menu>
      </RadixPopover.Root>
    </MultiSelectContext.Provider>
  );
}

interface TriggerProps
  extends Pick<
    MultiSelectProps,
    | 'aria-label'
    | 'emphasis'
    | 'intent'
    | 'invalid'
    | 'leadIcon'
    | 'placeholder'
    | 'placeholderVariant'
    | 'size'
  > {
  getDropdownProps: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  getToggleButtonProps: any; // eslint-disable-line @typescript-eslint/no-explicit-any
}

function Trigger({
  'aria-label': ariaLabel,
  emphasis = 'highlighted',
  getDropdownProps,
  getToggleButtonProps,
  intent = 'secondary',
  invalid,
  leadIcon: LeadIcon,
  placeholder,
  placeholderVariant,
  size = 'md',
}: TriggerProps) {
  const context = React.useContext(MultiSelectContext);
  const responsive = useResponsive();

  if (!context) {
    throw new Error('Missing context.');
  }

  const { closeMenu, disabled, isOpen, onBlur } = context;

  const styles = useSelectStyling({ emphasis, invalid, intent, size });
  const { spacing } = useFullTheme();

  return (
    <RadixPopover.Trigger
      asChild
      onKeyDown={event => {
        if (event.key === 'Escape' && isOpen) {
          // Stops this particular radix instance from handling the Esc keypress
          // See https://applyboard.atlassian.net/browse/UIE-1564
          onBlur?.();
          closeMenu();
          event.preventDefault();
        }
      }}
    >
      <div
        {...getToggleButtonProps(
          getDropdownProps({
            preventKeyAction: isOpen,
            disabled: disabled,
          }),
        )}
        aria-label={ariaLabel}
        css={styles}
        disabled={disabled}
        tabIndex={disabled ? '-1' : '0'}
      >
        <div
          css={[
            {
              display: 'flex',
              alignItems: 'center',
              flexGrow: 1,
              justifyContent: 'space-between',
              width: '100%',
            },
          ]}
        >
          <div
            css={[
              {
                display: 'flex',
                flexGrow: 1,
                height: 'auto',
                overflow: 'hidden',
                width: `calc(100% - 24px ${LeadIcon ? '- 24px - 16px' : ''})`,
              },
              responsive.css({
                gap: responsive.map(placeholderVariant, val =>
                  val === 'indicatorOnly' ? spacing[2] : spacing[4],
                ),
              }),
            ]}
          >
            {LeadIcon && (
              <IconWrapper contrast="high" disabled={disabled} intent={intent} selector="div">
                <LeadIcon />
              </IconWrapper>
            )}
            <Placeholder placeholder={placeholder} placeholderVariant={placeholderVariant} />
          </div>

          <IconWrapper contrast="high" disabled={disabled} intent={intent} selector="div">
            {isOpen ? <ChevronUpOutlineIcon /> : <ChevronDownOutlineIcon />}
          </IconWrapper>
        </div>
      </div>
    </RadixPopover.Trigger>
  );
}

interface MenuProps {
  children: MultiSelectProps['children'];
  getMenuProps: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  menuAlignment: MultiSelectProps['menuAlignment'];
}

function Menu({ children, getMenuProps, menuAlignment }: MenuProps) {
  const context = React.useContext(MultiSelectContext);
  if (!context) {
    throw new Error('Missing context.');
  }

  const { isOpen, closeMenu, onBlur } = context;
  const theme = useFullTheme();

  return (
    <RadixPopover.Content
      align={menuAlignment ?? 'start'}
      asChild
      forceMount
      sideOffset={8}
      onKeyDown={event => {
        if (event.key === 'Escape' && isOpen) {
          // Stops this particular radix instance from handling the Esc key event
          // See https://applyboard.atlassian.net/browse/UIE-1564
          closeMenu();
          onBlur?.();
          event.preventDefault();
        }
      }}
    >
      <ul
        {...getMenuProps()}
        css={{
          backgroundColor: theme.colors.bgRegular,
          borderRadius: theme.borderRadiuses.control,
          boxShadow: theme.elevations.z1,
          display: isOpen ? 'block' : 'none',
          margin: 0,
          maxHeight: `var(--radix-popover-content-available-height)`,
          minHeight: Math.min(React.Children.count(children) * 48, 200),
          minWidth: 300,
          overflowY: 'auto',
          paddingBlock: 0,
          paddingInline: 0,
          // `!important` is needed because Radix is setting `pointer-events: none` in a `style`
          // attribute, which overrides the `css` prop
          pointerEvents: 'all !important',
          width: `var(--radix-popover-trigger-width)`,
          zIndex: theme.zIndex.dialog.content,
          '::-webkit-scrollbar': {
            backgroundColor: 'transparent',
            width: theme.spacing[2],
          },
          '::-webkit-scrollbar-thumb': {
            backgroundColor: theme.colors.borderRegularHover,
            borderRadius: theme.borderRadiuses.container,
            marginRight: theme.spacing[1],
          },
        }}
      >
        {isOpen && children}
      </ul>
    </RadixPopover.Content>
  );
}

interface OptionProps {
  disabled?: Option['disabled'];
  label: Option['label'];
  value: Option['value'];
}

function Option({ disabled: _disabled = false, label, value }: OptionProps) {
  const context = React.useContext(MultiSelectContext);
  if (!context) {
    throw new Error('Missing context.');
  }

  const theme = useFullTheme();
  const { getItemProps, highlightedIndex, optionsWithGroups, onChange, values } = context;
  const disabled = context.disabled || _disabled;

  const isSelected = values.includes(value);
  const option = optionsWithGroups.find(otherOption => otherOption.value === value);
  const index = optionsWithGroups.findIndex(otherOption => otherOption === option);

  const itemProps = getItemProps({
    'aria-selected': isSelected,
    index,
    item: {
      disabled,
      label,
      value,
    },
    onClick: () => {
      onChange(isSelected ? values.filter(otherValue => otherValue !== value) : [...values, value]);
    },
  });

  const rounded = calculateRounded(index, optionsWithGroups.length, !!option?.group);

  return (
    <li
      {...itemProps}
      css={[
        {
          alignItems: 'center',
          backgroundColor: 'transparent',
          border: 0,
          borderRadius: 0,
          cursor: disabled ? 'not-allowed' : 'pointer',
          display: 'flex',
          gap: theme.spacing[2],
          listStyleType: 'none',
          padding: `${theme.spacing[3]} ${theme.spacing[4]}`,
        },
        ['top', 'all'].includes(rounded) && {
          borderTopLeftRadius: theme.borderRadiuses.control,
          borderTopRightRadius: theme.borderRadiuses.control,
        },
        ['bottom', 'all'].includes(rounded) && {
          borderBottomLeftRadius: theme.borderRadiuses.control,
          borderBottomRightRadius: theme.borderRadiuses.control,
        },
        disabled && {
          cursor: 'not-allowed',
        },
        !disabled &&
          highlightedIndex === index && {
            backgroundColor: theme.colors.bgRegularHover,
          },
        !disabled && {
          '&:focus': {
            // FIXME: for some reason this gets triggered on hover. I suspect it's to do with
            // bugs in the radix-ui/react-dropdown-menu package.
            boxShadow: `inset ${theme.colors.actionPrimary} 0px 0px 0px 1px, ${theme.colors.actionPrimary} 0px 0px 0px 1px`,
            outline: 'none',
          },
          '&:hover': {
            backgroundColor: theme.colors.bgRegularHover,
          },
          '&:active': {
            backgroundColor: theme.colors.bgRegularPressed,
          },
        },
        isSelected &&
          !disabled && {
            backgroundColor: theme.colors.bgPrimary,
            '&:hover': {
              backgroundColor: theme.colors.bgPrimaryHover,
            },
            '&:active': {
              backgroundColor: theme.colors.bgPrimaryPressed,
            },
          },
        isSelected &&
          disabled && {
            backgroundColor: theme.colors.bgPrimaryHighlight,
          },
        !disabled &&
          highlightedIndex === index &&
          isSelected && {
            backgroundColor: theme.colors.bgPrimaryHover,
          },
      ]}
    >
      <InternalCheckboxButton checked={isSelected} disabled={disabled} />
      <Text variant="bodyM" contrast={disabled ? 'low' : 'high'}>
        {label}
      </Text>
    </li>
  );
}

interface PlaceholderProps {
  placeholder: MultiSelectProps['placeholder'];
  placeholderVariant: MultiSelectProps['placeholderVariant'];
}

function Placeholder({ placeholder, placeholderVariant }: PlaceholderProps) {
  const theme = useFullTheme();
  const responsive = useResponsive();
  const context = React.useContext(MultiSelectContext);
  if (!context) {
    throw new Error('Missing context.');
  }

  const { disabled, groups, optionsWithGroups, values } = context;
  return (
    <>
      <div
        css={[
          {
            alignItems: 'center',
            display: 'flex',
            gap: theme.spacing[1],
            width: '100%',
          },
          responsive.css({
            display: responsive.map(placeholderVariant, val =>
              val === 'indicatorOnly' || val === 'indicator' ? 'flex' : 'none',
            ),
            justifyContent: responsive.map(placeholderVariant, val =>
              val === 'indicatorOnly' ? 'flex-end' : 'space-between',
            ),
            gap: responsive.map(placeholderVariant, val =>
              val === 'indicatorOnly' ? theme.spacing[0] : theme.spacing[1],
            ),
          }),
        ]}
      >
        <span
          css={[
            { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
            responsive.css({
              display: responsive.map(placeholderVariant, val =>
                val === 'indicatorOnly' ? 'none' : 'block',
              ),
            }),
          ]}
        >
          {placeholder}
        </span>
        {values.length > 0 && (
          <IconWrapper contrast="mid" disabled={disabled} intent="primary" selector="div">
            <BulletFillIcon />
          </IconWrapper>
        )}
      </div>

      <div
        css={[
          {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
          responsive.css({
            display: responsive.map(placeholderVariant, val =>
              val === 'groups' ? 'block' : 'none',
            ),
          }),
        ]}
      >
        {!values.length ? (
          <>{placeholder}</>
        ) : (
          <>
            {values
              .map(value => {
                const optWithGroup = optionsWithGroups.find(
                  otherOption => otherOption.value === value,
                );
                if (optWithGroup) {
                  const group = groups.find(otherGroup => otherGroup.value === optWithGroup.group);
                  if (group && placeholderVariant === 'groups') {
                    return `${group.label}/${optWithGroup.label}`;
                  } else {
                    return optWithGroup.label;
                  }
                }
              })
              .join(', ')}
          </>
        )}
      </div>
      <div
        css={[
          {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
          responsive.css({
            display: responsive.map(placeholderVariant, val =>
              val === 'truncate' ? 'block' : 'none',
            ),
          }),
        ]}
      >
        {!values.length ? (
          <>{placeholder}</>
        ) : (
          <>
            {values
              .map(value => {
                const optWithGroup = optionsWithGroups.find(
                  otherOption => otherOption.value === value,
                );
                if (optWithGroup) {
                  return optWithGroup.label;
                }
              })
              .join(', ')}
          </>
        )}
      </div>
    </>
  );
}

export type MultiSelectOptionProps = OptionProps;

MultiSelect.Option = function MultiSelectOption(props: MultiSelectOptionProps) {
  return <Option disabled={props.disabled} label={props.label} value={props.value} />;
};

export interface MultiSelectOptionGroupProps {
  children: React.ReactNode[];
  label: string;
  value: string;
}

MultiSelect.OptionGroup = function MultiSelectOptionGoup({
  children,
  label,
}: MultiSelectOptionGroupProps) {
  const context = React.useContext(MultiSelectContext);
  if (!context) {
    throw new Error('`MultiSelect.OptionGroup` can only be rendered inside `MultiSelect`');
  }

  const theme = useFullTheme();
  const { spacing } = theme;

  if (
    React.Children.toArray(children).filter(
      child =>
        child instanceof Object &&
        'type' in child &&
        (child.type === MultiSelect.Option || child.type === Divider),
    ).length !== React.Children.count(children)
  ) {
    throw new Error(
      'MultiSelect.OptionGroup only allows children of type MultiSelect.Option, and Divider',
    );
  }

  return (
    <li
      css={{
        display: 'list-item',
        listStyleType: 'none',
      }}
    >
      <ul css={{ padding: 0 }} role="group">
        <li
          css={{
            alignItems: 'center',
            borderRadius: theme.borderRadiuses.control,
            display: 'list-item',
            listStyleType: 'none',
            paddingBlock: spacing[2],
            paddingInline: spacing[2],
          }}
          role="presentation"
        >
          <Text variant="labelM">{label}</Text>
        </li>
        {children}
      </ul>
    </li>
  );
};

interface OptionWithGroup extends MultiSelectOptionProps {
  group?: string;
}

function mapOptionsToGroups(children: React.ReactNode | React.ReactNode[]): Array<OptionWithGroup> {
  const map = new Array<OptionWithGroup>();
  React.Children.toArray(children).forEach(child => {
    if (
      child instanceof Object &&
      'type' in child &&
      (child.type === MultiSelect.Option || child.type === MultiSelect.OptionGroup)
    ) {
      recursiveMapOptionsToGroups(child, map);
    }
  });

  return map;
}

const recursiveMapOptionsToGroups = (
  child: React.ReactChild,
  map: Array<OptionWithGroup>,
  group?: string,
): void => {
  const childReactElement = child as React.ReactElement;

  if (childReactElement.type === MultiSelect.OptionGroup) {
    React.Children.forEach(childReactElement.props.children, child => {
      recursiveMapOptionsToGroups(child, map, childReactElement.props.value);
    });
  } else if (childReactElement.type === MultiSelect.Option) {
    map.push({
      disabled: childReactElement.props.disabled,
      group,
      label: childReactElement.props.label,
      value: childReactElement.props.value,
    });
  }
};

const calculateRounded = (index: number, numItems: number, isInGroup = false) => {
  const isFirst = index === 0;
  const isLast = index === numItems - 1;

  if (isFirst && isLast) {
    return isInGroup ? 'bottom' : 'all';
  } else if (isFirst) {
    return isInGroup ? 'none' : 'top';
  } else if (isLast) {
    return 'bottom';
  }
  return 'none';
};
